import { Box, chakra, useColorMode, type BoxProps } from '@chakra-ui/react';
import { keyframes } from '@emotion/react';
import { useT } from '../../i18n';
import { useFestivalTouch } from '../../hooks/useFestivalTouch';
import { usePrefersReducedMotion } from '../../hooks/usePrefersReducedMotion';
import {
  FLAME_ORIGIN,
  FLAME_SWAY_MS,
  TOUCHES,
  TOUCH_VIEWBOX,
  shapePaint,
  type TouchId,
  type TouchShape,
} from '../../lib/festivalMode';
import { findOccasion, occasionName } from '../../lib/greetings';

// Festival mode (docs/indian-touches.md, D310): a small drawing beside the
// family's name on the home on a festival's own days. Drawn from
// shared/contracts/festival-touches.json, the same shapes Android and iOS
// draw; only the diya's flame moves, and not under reduced motion.

const sway = keyframes({
  from: { transform: 'scale(0.92)' },
  to: { transform: 'scale(1.04)' },
});

const G = chakra('g');

function Shape({ shape, dark }: { shape: TouchShape; dark: boolean }) {
  const { fill, stroke } = shapePaint(shape, dark);
  const common = {
    fill,
    stroke: stroke ?? undefined,
    strokeWidth: shape.strokeWidth,
    strokeLinecap: shape.cap,
    opacity: shape.opacity,
  };
  return shape.type === 'circle' ? (
    <circle cx={shape.cx} cy={shape.cy} r={shape.r} {...common} />
  ) : (
    <path d={shape.d} {...common} />
  );
}

interface TouchDrawingProps {
  touch: TouchId;
  size?: number;
  dark: boolean;
  still: boolean;
}

/** One touch, drawn at `size` px; exported for tests and previews. */
export function TouchDrawing({
  touch,
  size = 28,
  dark,
  still,
}: TouchDrawingProps) {
  const shapes = TOUCHES[touch];
  const flame = shapes.filter((shape) => shape.flicker);
  const rest = shapes.filter((shape) => !shape.flicker);
  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${TOUCH_VIEWBOX} ${TOUCH_VIEWBOX}`}
      aria-hidden="true"
      data-touch={touch}
    >
      {/* The flame sits behind the lamp's bowl, as in the file's order. */}
      {flame.length > 0 && (
        <G
          data-testid="touch-flame"
          sx={{
            transformBox: 'view-box',
            transformOrigin: `${FLAME_ORIGIN.x}px ${FLAME_ORIGIN.y}px`,
          }}
          animation={
            still
              ? undefined
              : `${sway} ${FLAME_SWAY_MS}ms ease-in-out infinite alternate`
          }
        >
          {flame.map((shape, index) => (
            <Shape key={index} shape={shape} dark={dark} />
          ))}
        </G>
      )}
      {rest.map((shape, index) => (
        <Shape key={index} shape={shape} dark={dark} />
      ))}
    </svg>
  );
}

/**
 * The home header's festival touch, or nothing: on a plain day, on a quiet
 * day, or with festival touches off in Settings (D312).
 */
export function FestivalTouch(props: BoxProps) {
  const t = useT();
  const today = useFestivalTouch();
  const still = usePrefersReducedMotion();
  const { colorMode } = useColorMode();
  if (!today) return null;
  const occasion = findOccasion(today.festival);
  const name = occasion ? occasionName(occasion) : today.festival;
  return (
    <Box
      as="span"
      role="img"
      aria-label={t('chat.home.festivalTouch', { name })}
      title={t('chat.home.festivalTouch', { name })}
      display="inline-flex"
      flexShrink={0}
      data-testid="festival-touch"
      data-festival={today.festival}
      {...props}
    >
      <TouchDrawing
        touch={today.touch}
        dark={colorMode === 'dark'}
        still={still}
      />
    </Box>
  );
}
